/* ============================================================
   storage — the file view.

   Selection follows the conventions people already have from
   every desktop file manager: click selects one, ctrl/cmd adds,
   shift takes the range, the header box takes everything, and
   Escape drops it. Dropping files uploads them here; dragging
   rows onto a folder moves them.

   The states that matter are all real states, not spinners:
   loading, empty, locked (someone else's secure folder),
   offline drive, and a failed request that can be retried.
   ============================================================ */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Icon, MarkerBadge, describeSkips, fileIconName, fmtBytes, fmtDate, isNoopMove, joinPath, parentOf, segmentsOf,
} from './lib';
import { api, ApiError } from './api';
import { entriesFromDrop } from './transfers';
import { ConfirmModal, MoveModal, NameModal } from './pickers';
import Menu from './Menu';
import ContextMenu from './ContextMenu';
import Properties from './Properties';
import { ShareDialog } from './Share';

const SORTS = {
  name: (a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }),
  size: (a, b) => (a.size || 0) - (b.size || 0),
  date: (a, b) => (a.created_at || 0) - (b.created_at || 0),
  owner: (a, b) => (a.owner || '').localeCompare(b.owner || ''),
};

const EMPTY_KEYS = new Set();

export default function Browser({
  token, me, drives, drive, path, onNavigate, onUpload, onDownload, onPreview, previewId,
  selection, onSelection, query, onQuery, onFileRows,
  clipboard, onClipboard, refreshKey, onChanged, toast,
}) {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(null);
  // Selection and filter belong to the URL, so the shell owns them and this
  // view is told what they are. Same shape as before — a Set of row keys and
  // a string — so everything below reads the same.
  const setSelection = onSelection;
  const setQuery = onQuery;
  const anchor = useRef(-1);           // shift-range origin; never rendered, so a ref
  const [sort, setSort] = useState({ key: 'name', dir: 1 });
  const [dragging, setDragging] = useState(false);
  const [dropTarget, setDropTarget] = useState(null);
  const [dragKeys, setDragKeys] = useState(() => new Set());
  const [dialog, setDialog] = useState(null);
  const [ctx, setCtx] = useState(null);      // {x, y, items} — the right-click menu
  const [props, setProps] = useState(null);  // what the properties dialog is showing
  const [sharing, setSharing] = useState(null);
  const [pasting, setPasting] = useState(false);
  const dragDepth = useRef(0);
  const listRef = useRef(null);
  const fileInput = useRef(null);
  const folderInput = useRef(null);

  const driveRow = drives.find((d) => d.id === drive);
  // What an admin allows on this drive (per-drive permissions). The server
  // enforces it; this only keeps buttons from offering what it would refuse.
  const can = (p) => !driveRow?.perms || driveRow.perms[p] !== false;
  const online = driveRow?.online !== false;

  /* The row behind the preview stays in view as the preview steps through
     the folder, so the list and the player never disagree about where you are. */
  useEffect(() => {
    if (!previewId) return;
    const el = document.querySelector('.st-row[data-open="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [previewId]);

  /* ── loading ───────────────────────────────────────────────────────── */
  const load = useCallback(async (signal) => {
    setLoading(true); setError(null); setLocked(null);
    try {
      const [fo, fi] = await Promise.all([
        api.folders(token, drive, path, signal),
        api.files(token, drive, path, signal),
      ]);
      setFolders(fo); setFiles(fi);
    } catch (e) {
      if (e.name === 'AbortError') return;
      if (e instanceof ApiError && e.locked) { setLocked(e.body); setFolders([]); setFiles([]); }
      else setError(e.message || 'Could not load this folder');
    } finally { setLoading(false); }
  }, [token, drive, path]);

  useEffect(() => {
    if (!online) { setLoading(false); setFolders([]); setFiles([]); return undefined; }
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load, online, refreshKey]);

  // The shell clears the selection and the filter when it navigates; the
  // range anchor is this view's own and goes with them.
  useEffect(() => { anchor.current = -1; }, [drive, path]);

  /* ── rows ──────────────────────────────────────────────────────────── */
  const rows = useMemo(() => {
    const f = folders.map((x) => ({
      kind: 'folder', id: x.id, key: `d:${x.id}`, label: x.name, raw: x,
      owner: x.owner_name, created_at: x.created_at, size: null,
      locked: x.locked, secure: x.secure, path: x.path, share: x.share,
      mine: x.owner_id === me?.id,
    }));
    const fl = files.map((x) => ({
      kind: 'file', id: x.id, key: `f:${x.id}`, label: x.original_name, raw: x,
      owner: x.owner_name, created_at: x.created_at, size: x.size, share: x.share,
      mine: x.owner_id === me?.id,
    }));
    const q = query.trim().toLowerCase();
    const filter = (r) => !q || r.label.toLowerCase().includes(q);
    const cmp = SORTS[sort.key] || SORTS.name;
    return [
      ...f.filter(filter).sort((a, b) => cmp(a, b) * sort.dir),
      ...fl.filter(filter).sort((a, b) => cmp(a, b) * sort.dir),
    ];
  }, [folders, files, query, sort, me]);

  const selected = useMemo(() => rows.filter((r) => selection.has(r.key)), [rows, selection]);
  // Every key this folder can offer, filter or no filter: typing in the
  // search box hides rows, it does not deselect them.
  const liveKeys = useMemo(
    () => new Set([...folders.map((f) => `d:${f.id}`), ...files.map((f) => `f:${f.id}`)]),
    [folders, files],
  );
  const selectedFiles = selected.filter((r) => r.kind === 'file');
  const canEditAll = selected.every((r) => r.mine || me?.role === 'admin') && can('delete');
  const allSelected = rows.length > 0 && rows.every((r) => selection.has(r.key));

  /* A selection that arrived in a link can name things that are not here any
     more. They are dropped rather than counted, and said out loud once —
     only for the set the link brought, since afterwards the only way to
     select something that has gone is to delete it yourself. */
  const settled = useRef(false);
  useEffect(() => {
    if (loading || error || locked || !online) return;
    const gone = [...selection].filter((k) => !liveKeys.has(k));
    const first = !settled.current;
    settled.current = true;
    if (!gone.length) return;
    setSelection(new Set([...selection].filter((k) => liveKeys.has(k))));
    if (first) {
      toast?.('info', gone.length === 1
        ? 'One of the selected items is not here'
        : `${gone.length} of the selected items are not here`,
      'They were renamed, moved or deleted since that link was made.');
    }
  }, [loading, error, locked, online, liveKeys, selection, setSelection, toast]);

  /* What the pane needs to step through the folder, for a file that was
     opened from a link and so arrived without its neighbours. */
  useEffect(() => {
    if (loading || error || locked || !online) return;
    onFileRows?.(rows.filter((r) => r.kind === 'file').map((r) => r.raw));
  }, [rows, loading, error, locked, online, onFileRows]);

  /* Where the list was scrolled to. This one is kept in the history entry
     rather than in the query string: it belongs to this visit and not to the
     link, and it has to survive Back as well as a refresh. Which element
     actually scrolls depends on the width — the page at this size, the list
     itself when something bounds its height — so the scroller is whichever
     of the two overflows. */
  const scroller = useCallback(() => {
    const el = listRef.current;
    if (el && el.scrollHeight > el.clientHeight + 4) return el;
    return document.scrollingElement || document.documentElement;
  }, []);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        try {
          window.history.replaceState(
            { ...(window.history.state || {}), scroll: Math.round(scroller().scrollTop) }, '');
        } catch { /* nothing on screen depends on it */ }
      });
    };
    const el = listRef.current;
    window.addEventListener('scroll', onScroll, { passive: true });
    el?.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      el?.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [scroller]);

  const restoredFor = useRef(null);
  useEffect(() => {
    const here = `${drive}\u0000${path}`;
    if (loading || restoredFor.current === here) return;
    restoredFor.current = here;
    const y = window.history.state?.scroll;
    if (y > 0) scroller().scrollTop = y;
  }, [drive, path, loading, rows.length, scroller]);

  /* ── selection ─────────────────────────────────────────────────────── */
  // Functional updates throughout: two clicks inside one frame (a fast
  // double-tap, a synthetic test, a stuck key) would otherwise both read the
  // selection as it was before either of them.
  const clickRow = (e, index) => {
    const row = rows[index];
    const shift = e.shiftKey;
    const additive = e.ctrlKey || e.metaKey;
    setSelection((prev) => {
      const next = new Set(prev);
      if (shift && anchor.current >= 0) {
        const a = anchor.current;
        const [lo, hi] = a < index ? [a, index] : [index, a];
        if (!additive) next.clear();
        for (let i = lo; i <= hi; i += 1) next.add(rows[i].key);
      } else if (additive) {
        if (next.has(row.key)) next.delete(row.key); else next.add(row.key);
      } else {
        next.clear(); next.add(row.key);
      }
      return next;
    });
    if (!shift) anchor.current = index;
  };

  const toggleOne = (key, index) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    anchor.current = index;
  };

  const toggleAll = () => {
    setSelection((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.key))));
  };

  /* ── keyboard ──────────────────────────────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || dialog) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'a') {
        e.preventDefault(); setSelection(new Set(rows.map((r) => r.key)));
      } else if (mod && e.key.toLowerCase() === 'c' && selected.length) {
        e.preventDefault(); putOnClipboard('copy', selected);
      } else if (mod && e.key.toLowerCase() === 'x' && selected.length) {
        e.preventDefault(); putOnClipboard('cut', selected);
      } else if (mod && e.key.toLowerCase() === 'v' && clipboard) {
        e.preventDefault(); paste(path);
      } else if (mod && e.shiftKey && e.key.toLowerCase() === 'n') {
        e.preventDefault(); askNewFolder(false);
      } else if (e.key === 'Escape') {
        setSelection(new Set());
      } else if (e.key === 'Delete' && selected.length) {
        // Shift+Delete skips the trash, as it does in every file manager —
        // and still asks first.
        e.preventDefault(); askDelete(selected, e.shiftKey);
      } else if (e.key === 'Enter' && e.altKey && selected.length) {
        e.preventDefault(); showProperties();
      } else if (e.key === 'F2' && selected.length === 1) {
        e.preventDefault(); askRename(selected[0]);
      } else if (e.key === 'Enter' && selected.length === 1 && selected[0].kind === 'folder') {
        e.preventDefault(); openFolder(selected[0]);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  /* ── actions ───────────────────────────────────────────────────────── */
  const openFolder = (row) => {
    if (row.locked) { toast?.('warn', 'Secure folder', `${row.raw.owner_name || 'Someone else'} made this one. Only they and admins can open it.`); return; }
    onNavigate(drive, row.path);
  };

  const refresh = () => onChanged();

  const askRename = (row) => setDialog({
    type: 'name',
    title: row.kind === 'folder' ? 'Rename folder' : 'Rename file',
    label: 'New name', initial: row.label, confirmLabel: 'Rename',
    onSubmit: async (name) => {
      if (row.kind === 'folder') await api.renameFolder(token, row.id, name);
      else await api.renameFile(token, row.id, name);
      refresh();
    },
  });

  /* Delete goes to the trash, with an Undo on the toast; ticking the box (or
     Shift+Delete) deletes for good. Something too big for the trash comes
     back as a second question rather than a silent permanent delete. */
  const runDelete = async (items, permanent) => {
    const res = await api.batchDelete(token,
      items.filter((i) => i.kind === 'file').map((i) => i.id),
      items.filter((i) => i.kind === 'folder').map((i) => i.id), permanent);
    const skipped = res.skipped || [];
    const count = (res.files || 0) + (res.folders || 0);
    const bigIds = new Set(skipped.filter((s) => s.reason === 'too_big_for_trash').map((s) => s.id));
    const kept = skipped.filter((s) => !bigIds.has(s.id));
    if (count) {
      const what = [res.files && `${res.files} file${res.files > 1 ? 's' : ''}`,
        res.folders && `${res.folders} folder${res.folders > 1 ? 's' : ''}`].filter(Boolean).join(' and ');
      if (res.trashed && res.batch) {
        toast?.('ok', 'Moved to the trash', what, {
          label: 'Undo',
          run: async () => {
            try {
              await api.trashRestore(token, { batch: res.batch });
              toast?.('ok', 'Restored', what);
            } catch (e) { toast?.('err', 'Could not undo', e.message); }
            refresh();
          },
        });
      } else {
        toast?.('ok', 'Deleted for good', what);
      }
    }
    if (kept.length) {
      const n = (r) => kept.filter((s) => s.reason === r).length;
      toast?.('warn', 'Some things were kept',
        [n('not_yours') && `${n('not_yours')} belong to someone else`,
          n('contains_others_files') && `${n('contains_others_files')} folder(s) hold other people's files`,
          n('drive_offline') && `${n('drive_offline')} on a drive that is offline`,
          n('failed') && `${n('failed')} could not be moved`]
          .filter(Boolean).join('; '));
    }
    setSelection(new Set());
    refresh();
    if (bigIds.size) {
      const big = items.filter((i) => bigIds.has(i.id));
      // After this dialog has closed, or its close would close the next one.
      window.setTimeout(() => setDialog({
        type: 'confirm',
        title: 'Too big for the trash',
        body: <p>{big.length === 1 ? big[0].label : `${big.length} items`} {big.length === 1 ? 'is' : 'are'} bigger
          than the trash can hold, so {big.length === 1 ? 'it' : 'they'} can only be deleted for good. This cannot be undone.</p>,
        confirmLabel: 'Delete for good',
        onConfirm: () => runDelete(big, true),
      }), 0);
    }
  };

  const askDelete = (items = selected, permanentFirst = false) => {
    if (!items.length) return;
    const folderCount = items.filter((i) => i.kind === 'folder').length;
    const fileCount = items.length - folderCount;
    const what = [fileCount && `${fileCount} file${fileCount > 1 ? 's' : ''}`,
      folderCount && `${folderCount} folder${folderCount > 1 ? 's' : ''}`].filter(Boolean).join(' and ');
    setDialog({
      type: 'confirm',
      title: `Delete ${items.length === 1 ? items[0].label : `${items.length} items`}`,
      option: { label: 'Delete for good — skip the trash', initial: permanentFirst },
      confirmLabel: (perm) => (perm ? 'Delete for good' : 'Move to trash'),
      danger: (perm) => perm,
      body: (perm) => (
        <>
          {perm ? (
            <p>{what} will be deleted from {driveRow?.label} for good.</p>
          ) : (
            <p>{what} will move to the trash on {driveRow?.label}. You can put {items.length === 1 ? 'it' : 'them'} back
              from Trash until {items.length === 1 ? 'it is' : 'they are'} cleared out.</p>
          )}
          {folderCount > 0 && <p className="st-warn-line">Everything inside {folderCount > 1 ? 'those folders' : 'the folder'} goes too.</p>}
          {perm && <p className="st-warn-line">This cannot be undone.</p>}
        </>
      ),
      onConfirm: (perm) => runDelete(items, perm),
    });
  };

  const askMove = () => setDialog({
    type: 'move',
    blocked: selected.filter((s) => s.kind === 'folder').map((s) => ({ drive, path: s.path })),
    onMove: async (destPath, destDrive) => {
      // Picking the folder you are already in is a no-op the server would
      // report as "1 folder moved", which is not what happened.
      if (destDrive === drive && destPath === path) { setSelection(new Set()); return; }
      const res = await api.batchMove(token,
        selected.filter((i) => i.kind === 'file').map((i) => i.id),
        selected.filter((i) => i.kind === 'folder').map((i) => i.id),
        destPath, destDrive);
      const skipped = res.skipped || [];
      if (skipped.length) {
        toast?.('warn', `${skipped.length} item(s) stayed put`, describeSkips(skipped));
      } else if ((res.files || 0) + (res.folders || 0)) {
        toast?.('ok', 'Moved', `${res.files} file(s), ${res.folders} folder(s)`);
      }
      setSelection(new Set());
      refresh();
    },
  });

  const askNewFolder = (secure) => setDialog({
    type: 'name',
    title: secure ? 'New secure folder' : 'New folder',
    label: 'Folder name', confirmLabel: 'Create',
    hint: secure
      ? 'Everyone will see this folder exists. Only you and admins can open it, put things in it, or delete it.'
      : 'Everyone can open it and download from it. Only the person who uploads a file can delete that file.',
    onSubmit: async (name) => {
      const res = await api.createFolder(token, drive, path, name, secure);
      if (res.existed) toast?.('info', 'Already there', `${name} exists in this folder.`);
      refresh();
    },
  });

  /** One entry point: a row, or whatever is selected. */
  const showProperties = (row) => {
    const picked = row && !selection.has(row.key) ? [row] : selected;
    if (picked.length > 1) setProps({ kind: 'selection', rows: picked, drive });
    else {
      const one = picked[0] || row;
      if (!one) return;
      setProps(one.kind === 'folder'
        ? { kind: 'folder', row: one, drive }
        : { kind: 'file', row: one, drive });
    }
  };

  const askNewTextFile = () => setDialog({
    type: 'name',
    title: 'New text file',
    label: 'File name', initial: 'untitled.txt', confirmLabel: 'Create',
    hint: 'A plain text file you can edit here. Add an extension (.md, .json, .sh) to say what it is.',
    onSubmit: async (name) => {
      const f = await api.createTextFile(token, drive, path, name, '');
      refresh();
      onPreview?.(f, [f]);
    },
  });

  /* ── clipboard ─────────────────────────────────────────────────────────
     Cut and copy only record what was picked; the work happens on paste,
     which is what makes "copy here, navigate away, paste there" work at all.
     A cut is a move — nothing leaves the drive until you paste it. */
  const putOnClipboard = (mode, items) => {
    const picked = items.length ? items : selected;
    if (!picked.length) return;
    onClipboard?.({
      mode,
      drive,
      path,
      files: picked.filter((r) => r.kind === 'file').map((r) => r.id),
      folders: picked.filter((r) => r.kind === 'folder').map((r) => r.id),
      count: picked.length,
      label: picked.length === 1 ? picked[0].label : `${picked.length} items`,
    });
    toast?.('info', mode === 'cut' ? 'Cut' : 'Copied',
      `${picked.length === 1 ? picked[0].label : `${picked.length} items`} — paste where you want ${picked.length === 1 ? 'it' : 'them'}.`);
  };

  const paste = async (destPath = path) => {
    if (!clipboard || pasting) return;
    if (clipboard.mode === 'cut' && clipboard.drive === drive && clipboard.path === destPath) {
      onClipboard?.(null);
      return;                                  // already there; nothing to do
    }
    setPasting(true);
    try {
      const res = clipboard.mode === 'cut'
        ? await api.batchMove(token, clipboard.files, clipboard.folders, destPath, drive)
        : await api.batchCopy(token, clipboard.files, clipboard.folders, destPath, drive);
      const n = (res.files || 0) + (res.folders || 0);
      const skipped = res.skipped || [];
      if (n) {
        toast?.('ok', clipboard.mode === 'cut' ? 'Moved' : 'Copied',
          `${res.files} file(s), ${res.folders} folder(s)`);
      }
      if (skipped.length) {
        toast?.('warn', `${skipped.length} item(s) stayed put`, describeSkips(skipped));
      }
      if (clipboard.mode === 'cut') onClipboard?.(null);
      refresh();
    } catch (e) {
      toast?.('err', clipboard.mode === 'cut' ? 'Move failed' : 'Copy failed', e.message);
    } finally { setPasting(false); }
  };

  const toggleSecure = async (row) => {
    try {
      await api.setFolderSecure(token, row.id, !row.secure);
      toast?.('ok', row.secure ? 'Folder opened up' : 'Folder secured',
        row.secure ? `${row.label} is readable by everyone again.`
                   : `Only you and admins can open ${row.label} now.`);
      refresh();
    } catch (e) { toast?.('err', 'Could not change it', e.message); }
  };

  const deleteOne = (row) => askDelete([row]);

  /* ── context menus ─────────────────────────────────────────────────────
     Right-clicking something that is not selected selects it first: every
     file manager works that way, and acting on a hidden selection is how you
     delete the wrong thing. */
  const openRowMenu = (e, row) => {
    e.preventDefault(); e.stopPropagation();
    let picked = selected;
    if (!selection.has(row.key)) {
      setSelection(new Set([row.key]));
      anchor.current = rows.indexOf(row);
      picked = [row];
    }
    const many = picked.length > 1;
    const mine = picked.every((r) => r.mine || me?.role === 'admin');
    const files = picked.filter((r) => r.kind === 'file');
    setCtx({
      x: e.clientX, y: e.clientY,
      items: [
        ...(row.kind === 'folder'
          ? [{ label: 'Open', icon: 'folder', onSelect: () => openFolder(row), disabled: row.locked }]
          : [{ label: 'Preview', icon: 'eye', hint: 'Click',
               onSelect: () => onPreview?.(row.raw, rows.filter((r) => r.kind === 'file').map((r) => r.raw)) }]),
        { label: files.length > 1 ? `Download ${files.length} files` : 'Download',
          icon: 'download', disabled: !files.length,
          onSelect: () => files.forEach((r) => onDownload(r.raw)) },
        'sep',
        { label: 'Cut', icon: 'cut', hint: 'Ctrl X', disabled: !mine,
          onSelect: () => putOnClipboard('cut', picked) },
        { label: 'Copy', icon: 'copy', hint: 'Ctrl C',
          onSelect: () => putOnClipboard('copy', picked) },
        { label: clipboard ? `Paste ${clipboard.label}` : 'Paste', icon: 'paste', hint: 'Ctrl V',
          disabled: !clipboard || (row.kind === 'folder' && row.locked && !row.mine),
          onSelect: () => paste(row.kind === 'folder' ? row.path : path) },
        'sep',
        { label: 'Rename', icon: 'edit', hint: 'F2', disabled: many || !mine || !can('upload'),
          onSelect: () => askRename(row) },
        { label: 'Move to…', icon: 'move', disabled: !mine || !can('delete'), onSelect: askMove },
        { label: row.share ? 'Sharing…' : 'Share publicly', icon: 'share',
          hint: row.share ? 'public' : undefined,
          disabled: many || !mine || !can('share'),
          onSelect: () => setSharing({ row, kind: row.kind }) },
        ...(row.share ? [{
          label: 'Copy public link', icon: 'copy',
          onSelect: () => {
            const url = `${window.location.origin}/s/${row.share}`;
            navigator.clipboard?.writeText(url)
              .then(() => toast?.('ok', 'Link copied', url))
              .catch(() => toast?.('info', 'Could not copy', url));
          },
        }] : []),
        ...(row.kind === 'folder' && !many
          ? [{ label: row.secure ? 'Make it open to everyone' : 'Make it secure',
               icon: row.secure ? 'unlock' : 'lock', disabled: !mine,
               onSelect: () => toggleSecure(row) }]
          : []),
        'sep',
        { label: many ? `Properties of ${picked.length} items` : 'Properties',
          icon: 'info', hint: 'Alt ↵', onSelect: () => showProperties(row) },
        { label: many ? `Delete ${picked.length} items` : 'Delete', icon: 'trash',
          hint: 'Del', danger: true, disabled: !mine || !can('delete'), onSelect: () => askDelete(picked) },
      ],
    });
  };

  const openBackgroundMenu = (e) => {
    if (e.target.closest?.('.st-row')) return;   // a row handles its own
    e.preventDefault();
    const writable = !locked && online && can('upload');
    setCtx({
      x: e.clientX, y: e.clientY,
      items: [
        { label: 'New folder', icon: 'plus', hint: 'Ctrl ⇧ N', disabled: !writable,
          onSelect: () => askNewFolder(false) },
        { label: 'New secure folder', icon: 'lock', disabled: !writable,
          onSelect: () => askNewFolder(true) },
        { label: 'New text file', icon: 'newfile', disabled: !writable,
          onSelect: askNewTextFile },
        'sep',
        { label: 'Upload files…', icon: 'upload', disabled: !writable,
          onSelect: () => fileInput.current?.click() },
        { label: 'Upload a folder…', icon: 'folder', disabled: !writable,
          onSelect: () => folderInput.current?.click() },
        'sep',
        { label: clipboard ? `Paste ${clipboard.label}` : 'Paste', icon: 'paste', hint: 'Ctrl V',
          disabled: !clipboard || !writable, onSelect: () => paste(path) },
        'sep',
        { label: 'Select everything', icon: 'check', hint: 'Ctrl A', disabled: !rows.length,
          onSelect: () => setSelection(new Set(rows.map((r) => r.key))) },
        { label: 'Refresh', icon: 'refresh', onSelect: refresh },
      ],
    });
  };

  /* ── drag and drop ─────────────────────────────────────────────────── */
  const ROWS_MIME = 'application/x-storage-rows';

  /* Two kinds of drag land on this view: files from the desktop, and rows
     being moved inside it. Only the first is an upload, so only the first
     raises the drop overlay — otherwise moving a file between folders looked
     like it was about to upload itself. */
  const isFileDrag = (e) => {
    const types = Array.from(e.dataTransfer?.types || []);
    return types.includes('Files') && !types.includes(ROWS_MIME);
  };

  const handleDrop = async (e, destPath = path, destDrive = drive) => {
    e.preventDefault(); e.stopPropagation();
    dragDepth.current = 0; setDragging(false); setDropTarget(null);
    const payload = e.dataTransfer.getData(ROWS_MIME);
    if (payload) {
      const moving = JSON.parse(payload);
      // Dropping something back where it already is — or a folder onto itself
      // — is not a move. Saying "moved 0 files" and reloading is noise; asking
      // the server is worse, because the only answer it has is a refusal.
      if (isNoopMove(moving, destPath, destDrive)) return;
      try {
        const res = await api.batchMove(token, moving.files, moving.folders, destPath, destDrive);
        const moved = (res.files || 0) + (res.folders || 0);
        if (moved) {
          toast?.('ok', 'Moved', `${res.files} file(s), ${res.folders} folder(s)`);
          setSelection(new Set());
          refresh();
        } else if (res.skipped?.length) {
          toast?.('warn', 'Nothing moved', describeSkips(res.skipped));
        }
      } catch (ex) { toast?.('err', 'Move failed', ex.message); }
      return;
    }
    const entries = await entriesFromDrop(e.dataTransfer);
    if (!entries.length) return;
    onUpload(entries, { drive: destDrive, path: destPath });
  };

  const startRowDrag = (e, row) => {
    // Grabbing a row that is part of the selection drags the whole selection;
    // grabbing one outside it makes that row the selection first, so what you
    // see highlighted is always exactly what you are about to move.
    const inSelection = selection.has(row.key);
    const keys = inSelection ? selected : [row];
    if (!inSelection) { setSelection(new Set([row.key])); anchor.current = rows.indexOf(row); }
    if (!keys.every((k) => k.mine || me?.role === 'admin')) {
      e.preventDefault();
      toast?.('warn', 'Not yours to move', 'Only the owner or an admin can move these.');
      return;
    }
    setDragging(false);
    dragDepth.current = 0;
    e.dataTransfer.setData(ROWS_MIME, JSON.stringify({
      drive, path,
      files: keys.filter((k) => k.kind === 'file').map((k) => k.id),
      folders: keys.filter((k) => k.kind === 'folder').map((k) => k.id),
      // Carried so a drop can tell "into its own subtree" apart from a real
      // move without a round trip that can only come back as a refusal.
      folderPaths: keys.filter((k) => k.kind === 'folder').map((k) => k.path),
    }));
    e.dataTransfer.effectAllowed = 'move';

    // The default drag image is a picture of the one row under the cursor, so a
    // ten-file drag looks identical to a one-file drag. Carry the count instead.
    if (keys.length > 1 && e.dataTransfer.setDragImage) {
      const ghost = document.createElement('div');
      ghost.className = 'st-dragghost';
      ghost.textContent = `${keys.length} items`;
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, -12, -12);
      window.setTimeout(() => ghost.remove(), 0);
    }
    setDragKeys(new Set(keys.map((k) => k.key)));
  };

  const endRowDrag = () => setDragKeys(EMPTY_KEYS);

  /* ── render ────────────────────────────────────────────────────────── */
  const crumbs = segmentsOf(path);
  const ctxMenu = ctx && (
    <ContextMenu x={ctx.x} y={ctx.y} items={ctx.items} onClose={() => setCtx(null)}/>
  );

  return (
    <section
      className={`st-browser ${dragging ? 'is-dragging' : ''}`}
      onDragEnter={(e) => {
        if (!isFileDrag(e)) return;
        e.preventDefault(); dragDepth.current += 1; setDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = e.dataTransfer.types.includes(ROWS_MIME) ? 'move' : 'copy';
      }}
      onDragLeave={(e) => {
        if (!isFileDrag(e)) return;
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false); }
      }}
      onDrop={(e) => handleDrop(e)}
    >
      <header className="st-bar">
        <nav className="st-crumbs" aria-label="Breadcrumb">
          {/* Each crumb takes a drop, so moving something up a level is a drag
              to the folder's name rather than a trip through the Move dialog. */}
          <button type="button"
                  className={`st-crumb ${dropTarget === '/' ? 'is-drop' : ''}`}
                  onClick={() => onNavigate(drive, '/')}
                  onDragOver={(e) => { if (path !== '/') { e.preventDefault(); e.stopPropagation(); setDropTarget('/'); } }}
                  onDragLeave={() => setDropTarget((t) => (t === '/' ? null : t))}
                  onDrop={(e) => handleDrop(e, '/', drive)}>
            {driveRow?.label || drive}
          </button>
          {crumbs.map((seg, i) => {
            const to = '/' + crumbs.slice(0, i + 1).join('/');
            const isHere = to === path;
            return (
              <React.Fragment key={seg + i}>
                <span className="st-crumb-sep">/</span>
                <button type="button"
                        className={`st-crumb ${dropTarget === to ? 'is-drop' : ''}`}
                        onClick={() => onNavigate(drive, to)}
                        onDragOver={(e) => { if (!isHere) { e.preventDefault(); e.stopPropagation(); setDropTarget(to); } }}
                        onDragLeave={() => setDropTarget((t) => (t === to ? null : t))}
                        onDrop={(e) => handleDrop(e, to, drive)}>
                  {seg}
                </button>
              </React.Fragment>
            );
          })}
          <span className="st-crumb-count meta">
            {rows.length} item{rows.length === 1 ? '' : 's'}
          </span>
        </nav>

        <div className="st-bar-tools">
          <label className="st-search">
            <Icon name="search" size={14}/>
            <input className="input st-search-input" placeholder="Filter" value={query}
                   onChange={(e) => setQuery(e.target.value)} aria-label="Filter this folder"/>
            {query && (
              <button type="button" className="st-search-clear" aria-label="Clear filter" onClick={() => setQuery('')}>
                <Icon name="close" size={12}/>
              </button>
            )}
          </label>
          <Menu label="New" icon="plus" disabled={!online || !can('upload')} items={[
            { label: 'Folder', icon: 'folder', hint: 'everyone can open it', onSelect: () => askNewFolder(false) },
            { label: 'Secure folder', icon: 'lock', hint: 'only you and admins', onSelect: () => askNewFolder(true) },
          ]}/>
          <Menu label="Upload" icon="upload" primary disabled={!online || !can('upload')} items={[
            { label: 'Files…', icon: 'file', onSelect: () => fileInput.current?.click() },
            { label: 'Folder…', icon: 'folder', hint: 'keeps its structure', onSelect: () => folderInput.current?.click() },
          ]}/>
          <input ref={fileInput} type="file" multiple hidden onChange={(e) => {
            onUpload(Array.from(e.target.files).map((file) => ({ file, relPath: file.name })), { drive, path });
            e.target.value = '';
          }}/>
          <input ref={folderInput} type="file" multiple hidden webkitdirectory="" directory="" onChange={(e) => {
            onUpload(Array.from(e.target.files).map((file) => ({ file, relPath: file.webkitRelativePath || file.name })), { drive, path });
            e.target.value = '';
          }}/>
        </div>
      </header>

      {selected.length > 0 && (
        <div className="st-selbar">
          <span className="st-selbar-count">{selected.length}<span className="st-selbar-word"> selected</span></span>
          <span className="st-selbar-div" aria-hidden="true"/>
          <div className="st-selbar-acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => selectedFiles.forEach((r) => onDownload(r.raw))}
                  disabled={!selectedFiles.length}>
            <Icon name="download" size={14}/> <span className="st-selbar-lbl">Download</span>
          </button>
          {selected.length === 1 && (selected[0].mine || me?.role === 'admin') && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => askRename(selected[0])}>
              <Icon name="edit" size={14}/> <span className="st-selbar-lbl">Rename</span>
            </button>
          )}
          {selected.length === 1 && selected[0].kind === 'folder' && (selected[0].mine || me?.role === 'admin') && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => toggleSecure(selected[0])}>
              <Icon name={selected[0].secure ? 'unlock' : 'lock'} size={14}/>
              <span className="st-selbar-lbl">{selected[0].secure ? 'Make open' : 'Make secure'}</span>
            </button>
          )}
          <button type="button" className="btn btn--ghost btn--sm" onClick={askMove} disabled={!canEditAll}>
            <Icon name="move" size={14}/> <span className="st-selbar-lbl">Move</span>
          </button>
          <button type="button" className="btn btn--danger btn--sm" onClick={() => askDelete()} disabled={!canEditAll}>
            <Icon name="trash" size={14}/> <span className="st-selbar-lbl">Delete</span>
          </button>
          </div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => showProperties()}>
            <Icon name="info" size={14}/> <span className="st-selbar-lbl">Properties</span>
          </button>
          {!canEditAll && (
            <span className="st-selbar-note" title="Only the owner or an admin can move, rename or delete these">
              <Icon name="warn" size={13}/> not all yours
            </span>
          )}
          <span className="st-selbar-div" aria-hidden="true"/>
          <button type="button" className="iconbtn st-selbar-clear" aria-label="Clear selection"
                  title="Clear selection" onClick={() => setSelection(new Set())}>
            <Icon name="close" size={14}/>
          </button>
        </div>
      )}

      <div className="st-listhead" role="row">
        <label className="st-check" title={allSelected ? 'Deselect all' : 'Select all'}>
          <input type="checkbox" checked={allSelected} ref={(el) => { if (el) el.indeterminate = selection.size > 0 && !allSelected; }}
                 onChange={toggleAll} aria-label={allSelected ? 'Deselect all' : 'Select all'} disabled={!rows.length}/>
        </label>
        {[['name', 'Name'], ['owner', 'Added by'], ['size', 'Size'], ['date', 'Added']].map(([key, label]) => (
          <button key={key} type="button" className={`st-sort ${sort.key === key ? 'is-on' : ''}`}
                  onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }))}>
            {label}{sort.key === key ? (sort.dir > 0 ? ' ↑' : ' ↓') : ''}
          </button>
        ))}
        <span className="st-listhead-actions"/>
      </div>

      <div className="st-list" ref={listRef} onContextMenu={openBackgroundMenu}>
        {!online && (
          <div className="empty st-empty">
            <Icon name="warn" size={22}/>
            <p><strong>{driveRow?.label} is offline.</strong></p>
            <p className="meta">Its files are safe on the other machine — they just cannot be reached from here right now.</p>
            <button type="button" className="btn btn--ghost btn--sm" onClick={refresh}>Try again</button>
          </div>
        )}

        {online && loading && (
          <div className="st-skeletons">
            {[0, 1, 2, 3, 4].map((i) => <div key={i} className="skeleton st-skel-row"/>)}
          </div>
        )}

        {online && !loading && locked && (
          <div className="empty st-empty">
            <Icon name="lock" size={22}/>
            <p><strong>This folder is secure.</strong></p>
            <p className="meta">
              {locked.owner ? `${locked.owner} made it.` : 'Someone else made it.'} Only they and admins can open it.
              You can still see that it exists.
            </p>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => onNavigate(drive, '/')}>Back to {driveRow?.label}</button>
          </div>
        )}

        {online && !loading && error && (
          <div className="empty st-empty">
            <Icon name="warn" size={22}/>
            <p><strong>{error}</strong></p>
            <button type="button" className="btn btn--ghost btn--sm" onClick={refresh}>Try again</button>
          </div>
        )}

        {/* At the root there is nowhere to go up to, but the row still has to
            exist: without it every list started 44px higher than the same
            list one folder down, so the first row moved under the pointer
            the moment you navigated. It is inert and says where you are. */}
        {online && !loading && !locked && !error && path === '/' && (
          <div className="listrow st-row st-row--up is-top" aria-hidden="true">
            <span className="st-check"/>
            <span className="st-name">
              <Icon name="drive" size={16} className="st-icon-folder"/>
              <span className="st-name-text">Top of {driveRow?.label || drive}</span>
            </span>
            <span className="st-owner"/>
            <span className="st-size"/>
            <span className="st-date"/>
            <span className="st-actions"/>
          </div>
        )}

        {online && !loading && !locked && !error && path !== '/' && (
          <div
            className={`listrow st-row st-row--up ${dropTarget === parentOf(path) ? 'is-drop' : ''}`}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDropTarget(parentOf(path)); }}
            onDragLeave={() => setDropTarget((t) => (t === parentOf(path) ? null : t))}
            onDrop={(e) => handleDrop(e, parentOf(path), drive)}
            onClick={() => onNavigate(drive, parentOf(path))}
          >
            <span className="st-check"/>
            <button type="button" className="st-name" onClick={(e) => { e.stopPropagation(); onNavigate(drive, parentOf(path)); }}>
              <Icon name="folderUp" size={16} className="st-icon-folder"/>
              <span className="st-name-text">Up one level</span>
            </button>
            <span className="st-owner meta">{parentOf(path) === '/' ? driveRow?.label : parentOf(path)}</span>
            <span className="st-size"/>
            <span className="st-date"/>
            <span className="st-actions"/>
          </div>
        )}

        {online && !loading && !locked && !error && rows.length === 0 && (
          <div className="empty st-empty">
            <Icon name="upload" size={22}/>
            <p><strong>{query ? 'Nothing matches that.' : 'This folder is empty.'}</strong></p>
            {!query && <p className="meta">Drop files here, or use Upload.</p>}
          </div>
        )}

        {online && !loading && rows.map((row, index) => (
          <div
            key={row.key}
            role="row"
            draggable={row.mine || me?.role === 'admin'}
            onDragStart={(e) => startRowDrag(e, row)}
            onDragEnd={endRowDrag}
            onDragOver={(e) => { if (row.kind === 'folder' && !row.locked) { e.preventDefault(); e.stopPropagation(); setDropTarget(row.path); } }}
            onDragLeave={() => setDropTarget((t) => (t === row.path ? null : t))}
            onDrop={(e) => { if (row.kind === 'folder' && !row.locked) handleDrop(e, row.path, drive); }}
            data-open={row.kind === 'file' && row.raw.id === previewId ? 'true' : undefined}
            aria-current={row.kind === 'file' && row.raw.id === previewId ? 'true' : undefined}
            className={`listrow st-row ${row.kind === 'file' && row.raw.id === previewId ? 'is-open' : ''} ${selection.has(row.key) ? 'is-sel' : ''} ${dragKeys.has(row.key) ? 'is-dragging' : ''} ${dropTarget === row.path ? 'is-drop' : ''} ${row.locked ? 'is-locked' : ''}`}
            onClick={(e) => clickRow(e, index)}
            onDoubleClick={() => (row.kind === 'folder'
              ? openFolder(row)
              : onPreview?.(row.raw, rows.filter((r) => r.kind === 'file').map((r) => r.raw)))}
            onContextMenu={(e) => openRowMenu(e, row)}
          >
            <label className="st-check" onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" checked={selection.has(row.key)} onChange={() => toggleOne(row.key, index)}
                     aria-label={`Select ${row.label}`}/>
            </label>

            <button type="button" className="st-name"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (row.kind === 'folder') openFolder(row);
                      else onPreview?.(row.raw, rows.filter((r) => r.kind === 'file').map((r) => r.raw));
                    }}>
              <Icon name={row.kind === 'folder' ? (row.secure ? 'folderLock' : 'folder') : fileIconName(row.raw.mime_type, row.label)}
                    size={16} className={row.kind === 'folder' ? 'st-icon-folder' : 'st-icon-file'}/>
              <span className="st-name-text" title={row.label}>{row.label}</span>
              {row.secure && <span className="badge st-badge-secure" title="Only the owner and admins can open this">secure</span>}
              {row.kind === 'file' && <MarkerBadge count={row.raw.markers}/>}
              {row.share && (
                <span className="st-badge-share" title="Public — anyone with the link can download this">
                  <Icon name="share" size={12}/>
                </span>
              )}
            </button>

            <span className="st-owner meta">{row.mine ? 'you' : row.owner || '—'}</span>
            <span className="st-size tnum">{row.size == null ? '' : fmtBytes(row.size)}</span>
            <span className="st-date meta">{fmtDate(row.created_at)}</span>

            <span className="st-actions" onClick={(e) => e.stopPropagation()}>
              {row.kind === 'file' && (
                <button type="button" className="iconbtn" title="Download" aria-label={`Download ${row.label}`}
                        onClick={() => onDownload(row.raw)}><Icon name="download" size={15}/></button>
              )}
              {row.kind === 'folder' && (row.mine || me?.role === 'admin') && (
                <button type="button" className="iconbtn" title={row.secure ? 'Make it readable by everyone' : 'Make it secure'}
                        aria-label={row.secure ? 'Unsecure folder' : 'Secure folder'}
                        onClick={() => toggleSecure(row)}><Icon name={row.secure ? 'unlock' : 'lock'} size={15}/></button>
              )}
              {(row.mine || me?.role === 'admin') && (
                <>
                  <button type="button" className="iconbtn" title="Rename" aria-label={`Rename ${row.label}`}
                          onClick={() => askRename(row)}><Icon name="edit" size={15}/></button>
                  <button type="button" className="iconbtn iconbtn--danger" title="Delete" aria-label={`Delete ${row.label}`}
                          onClick={() => deleteOne(row)}><Icon name="trash" size={15}/></button>
                </>
              )}
            </span>
          </div>
        ))}
      </div>

      {dragging && (
        <div className="st-dropzone" aria-hidden="true">
          <div className="st-dropzone-inner">
            <Icon name="upload" size={28}/>
            <p><strong>Drop to upload</strong></p>
            <p className="meta">into {path === '/' ? driveRow?.label : path} · folders keep their shape</p>
          </div>
        </div>
      )}

      {dialog?.type === 'name' && (
        <NameModal {...dialog} onClose={() => setDialog(null)}/>
      )}
      {dialog?.type === 'confirm' && (
        <ConfirmModal {...dialog} onClose={() => setDialog(null)}/>
      )}
      {dialog?.type === 'move' && (
        <MoveModal token={token} drives={drives} fromDrive={drive} blocked={dialog.blocked}
                   onMove={dialog.onMove} onClose={() => setDialog(null)}/>
      )}

      {ctxMenu}

      {sharing && (
        <ShareDialog token={token} row={sharing.row} kind={sharing.kind} toast={toast}
                     onChanged={refresh} onClose={() => setSharing(null)}/>
      )}

      {props && (
        <Properties token={token} target={props} me={me} drives={drives}
                    onClose={() => setProps(null)}/>
      )}
    </section>
  );
}

export { joinPath };
